import { Injectable, computed, signal, Signal } from '@angular/core';

/** Estado de un spinner */
export type SpinnerStatus = 'idle' | 'loading' | 'completed';

/** Configuración por defecto */
const DEFAULT_COMPLETE_DELAY = 800;

/**
 * Servicio para manejar estados de spinners inline (embebidos en componentes).
 * A diferencia del LoadingStateService (overlay global), este es para
 * spinners que aparecen en posiciones específicas del UI.
 *
 * @example
 * ```typescript
 * // En el componente
 * private readonly spinner = inject(SpinnerStateService);
 *
 * async loadData() {
 *   this.spinner.start('my-data');
 *   const data = await this.service.fetchData();
 *   this.spinner.complete('my-data'); // Muestra ✓ antes de desaparecer
 * }
 * ```
 *
 * @example
 * ```html
 * <!-- En el template, donde quieras que aparezca -->
 * <app-spinner key="my-data" message="Cargando..." />
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class SpinnerStateService {
  /** Map de keys con su estado actual */
  private readonly _states = signal<Map<string, SpinnerStatus>>(new Map());

  /**
   * Obtiene el estado de una key específica
   * @param key Identificador de la operación
   * @returns Signal con el estado actual
   */
  getStatus(key: string): Signal<SpinnerStatus> {
    return computed(() => this._states().get(key) ?? 'idle');
  }

  /**
   * Verifica si una key está activa (loading o completed)
   * @param key Identificador de la operación
   * @returns Signal booleano que indica si está visible
   */
  isActive(key: string): Signal<boolean> {
    return computed(() => {
      const status = this._states().get(key);
      return status === 'loading' || status === 'completed';
    });
  }

  /**
   * Verifica si una key está cargando
   * @param key Identificador de la operación
   * @returns Signal booleano que indica si está cargando
   */
  isLoading(key: string): Signal<boolean> {
    return computed(() => this._states().get(key) === 'loading');
  }

  /**
   * Verifica si una key está completada
   * @param key Identificador de la operación
   * @returns Signal booleano que indica si está completada
   */
  isCompleted(key: string): Signal<boolean> {
    return computed(() => this._states().get(key) === 'completed');
  }

  /**
   * Inicia el spinner para una key
   * @param key Identificador único de la operación
   */
  start(key: string): void {
    this._states.update((map) => new Map(map).set(key, 'loading'));
  }

  /**
   * Completa el spinner mostrando ✓ antes de desaparecer
   * @param key Identificador de la operación
   * @param delay Tiempo en ms antes de ocultar (default: 800ms)
   */
  complete(key: string, delay: number = DEFAULT_COMPLETE_DELAY): void {
    this._states.update((map) => new Map(map).set(key, 'completed'));

    setTimeout(() => {
      this._states.update((map) => {
        const updated = new Map(map);
        updated.delete(key);
        return updated;
      });
    }, delay);
  }

  /**
   * Detiene el spinner inmediatamente (sin mostrar ✓)
   * @param key Identificador de la operación
   */
  stop(key: string): void {
    this._states.update((map) => {
      const updated = new Map(map);
      updated.delete(key);
      return updated;
    });
  }
}
